import { Component } from '@theme/component';

/**
 * @typedef {Object} NcnSocialProofBarRefs
 * @property {HTMLElement} count - Span displaying the formatted review count
 * @property {HTMLElement} [srRating] - Visually hidden span with screen-reader rating text
 */

/**
 * Social proof bar web component.
 * Fetches aggregate review data from Okendo and updates the star display
 * and review count. Falls back to Liquid-rendered defaults on failure.
 *
 * @extends {Component<NcnSocialProofBarRefs>}
 */
class NcnSocialProofBar extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number} Cache TTL in milliseconds (12 hours) */
  static #CACHE_TTL = 12 * 60 * 60 * 1000;

  connectedCallback() {
    super.connectedCallback();
    const { storeId, reviewSource } = this.dataset;
    if (storeId) this.#fetchReviewData(storeId, reviewSource === 'all');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Fetches aggregate review data from the Okendo API.
   *
   * @param {string} storeId
   * @param {boolean} allReviews - true = all reviews, false = site reviews only
   */
  async #fetchReviewData(storeId, allReviews) {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const base = `https://api.okendo.io/v1/stores/${encodeURIComponent(storeId)}`;
    const url = allReviews
      ? `${base}/review_aggregate`
      : `${base}/products/shopify-site-reviews-${storeId}/review_aggregate`;

    const cacheKey = `ncn-review-bar-${storeId}-${allReviews ? 'all' : 'site'}`;

    try {
      const cached = this.#readCache(cacheKey);
      const { reviewAggregate } = cached ?? await (async () => {
        const res = await fetch(url, { signal });
        if (!res.ok) return {};
        const data = await res.json();
        if (data.reviewAggregate) this.#writeCache(cacheKey, data);
        return data;
      })();
      const { reviewCount, reviewRatingValuesTotal } = reviewAggregate ?? {};
      if (!reviewCount) return;

      if (this.refs.count) {
        this.refs.count.textContent = this.#formatCount(reviewCount);
      }

      if (!reviewRatingValuesTotal) return;
      const avg = reviewRatingValuesTotal / reviewCount;

      if (this.refs.srRating) {
        const template = this.dataset.ratingSrTemplate ?? '__RATING__ out of 5 stars';
        this.refs.srRating.textContent = template.replace('__RATING__', avg.toFixed(1));
      }

      this.#updateStars(avg);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('[ncn-site-review-bar] Review data fetch failed:', error);
      }
    } finally {
      this.removeAttribute('data-loading');
    }
  }

  /**
   * Updates star fill states based on the average rating.
   * Stars start fully filled in the Liquid render; JS corrects them.
   *
   * @param {number} avg
   */
  #updateStars(avg) {
    const stars = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-star]'));
    const floor = Math.floor(avg);
    const decimal = avg % 1;

    for (const starEl of stars) {
      const index = Number(starEl.dataset.star);
      const fillPath = starEl.querySelector('[data-fill]');
      if (!fillPath) continue;

      if (index <= floor) {
        fillPath.setAttribute('fill', 'var(--ncn-spb-star-filled-color)');
      } else if (index === floor + 1 && decimal > 0) {
        const gradId = starEl.querySelector('linearGradient')?.id;
        fillPath.setAttribute('fill', gradId ? `url(#${gradId})` : 'none');
      } else {
        fillPath.setAttribute('fill', 'none');
      }
    }
  }

  /**
   * Formats a review count for display (e.g., 3200 → "3k+").
   *
   * @param {number} count
   * @returns {string}
   */
  #formatCount(count) {
    if (count >= 1000) return `${Math.floor(count / 1000)}k+`;
    return String(count);
  }

  /**
   * Reads a cached entry from localStorage. Returns null if missing or expired.
   *
   * @param {string} key
   * @returns {Object | null}
   */
  #readCache(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      if (Date.now() - entry.ts > NcnSocialProofBar.#CACHE_TTL) {
        localStorage.removeItem(key);
        return null;
      }
      return entry.data;
    } catch {
      return null;
    }
  }

  /**
   * Writes data to localStorage with a timestamp.
   *
   * @param {string} key
   * @param {Object} data
   */
  #writeCache(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch {
      /* storage full — silently skip */
    }
  }
}

if (!customElements.get('ncn-site-review-bar')) {
  customElements.define('ncn-site-review-bar', NcnSocialProofBar);
}
